// Global Instructions Rule Applied!
//
// useEmailCampaignCopies — manages a campaign's Cc/Bcc copy recipients for the
// compose form. Mirrors useEmailRecipients: module-local state, no Redux pre-
// fetch; cold-loads the persisted list on mount (campaignId from the form URL
// is enough — CLAUDE.md hard-refresh rule), and add/remove persist immediately
// (draft-only, enforced in the RPC) then refetch the canonical list.

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type {
  EmailCampaignCopy,
  EmailCampaignCopyKind
} from '../../../state_manager/data_models/email_campaign_copy_model.ts'
import {
  fetchCampaignCopies,
  addCampaignCopy,
  removeCampaignCopy,
  searchContactsForCopy,
  type CopyContactOption
} from '../services/emailService.ts'

export interface UseEmailCampaignCopiesOptions {
  /** The campaign whose Cc/Bcc this hook manages. Null = compose form is in
   *  'new' mode (no draft persisted yet); the hook stays inert. */
  campaignId: string | null
}

export const useEmailCampaignCopies = ({ campaignId }: UseEmailCampaignCopiesOptions) => {
  const [cc, setCc] = useState<EmailCampaignCopy[]>([])
  const [bcc, setBcc] = useState<EmailCampaignCopy[]>([])
  const [loading, setLoading] = useState(false)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const refetch = useCallback(async () => {
    if (!campaignId) {
      setCc([])
      setBcc([])
      return
    }
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    const res = await fetchCampaignCopies(campaignId, token)
    if (cancelledRef.current) return
    if (res.error && process.env.NODE_ENV === 'development') console.warn(res.error)
    setCc(res.data.cc)
    setBcc(res.data.bcc)
    setLoading(false)
  }, [campaignId])

  useEffect(() => {
    if (campaignId) refetch()
  }, [campaignId, refetch])

  /** Add one Cc/Bcc copy (contact-linked or manual email). Persists, then
   *  refetches so the editor shows the canonical persisted set. Returns an
   *  error string on failure, null on success. */
  const addCopy = useCallback(
    async (
      kind: EmailCampaignCopyKind,
      payload: { contact_id?: string; email_address?: string }
    ): Promise<string | null> => {
      if (!campaignId) return 'Save the draft before adding Cc/Bcc recipients.'
      const token = getAccessToken(store.getState().auth)
      const res = await addCampaignCopy(campaignId, { kind, ...payload }, token)
      if (cancelledRef.current) return null
      if (res.error) return res.error
      await refetch()
      return null
    },
    [campaignId, refetch]
  )

  /** Soft-delete one Cc/Bcc copy by id. */
  const removeCopy = useCallback(
    async (copyId: string): Promise<string | null> => {
      const token = getAccessToken(store.getState().auth)
      const res = await removeCampaignCopy(copyId, token)
      if (cancelledRef.current) return null
      if (res.error) return res.error
      await refetch()
      return null
    },
    [refetch]
  )

  /** Global contact typeahead for the picker (reuses /contacts/search). */
  const searchContacts = useCallback(async (term: string): Promise<CopyContactOption[]> => {
    const token = getAccessToken(store.getState().auth)
    const res = await searchContactsForCopy(term, token)
    if (cancelledRef.current) return []
    if (res.error && process.env.NODE_ENV === 'development') console.warn(res.error)
    return res.data
  }, [])

  return { cc, bcc, loading, refetch, addCopy, removeCopy, searchContacts }
}
